import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, ArrowRight, Loader2, ShieldCheck } from "lucide-react";

/**
 * TRCA Qualifier — landing page shown when checkout is cancelled / fails / expires.
 * Re-pitches the assessment value and lets the user retry the paid flow.
 */
export default function TrcaQualifier() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const retry = async () => {
    if (!user) { nav("/login?redirect=/trca-qualifier"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/checkout/session", {
        product_slug: "trca_assessment",
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary py-12 px-4" data-testid="trca-qualifier-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-[11px] uppercase tracking-[0.2em] text-ink-muted hover:text-ink">
          ← TWIN home
        </Link>

        <div className="bg-white border border-ink-line rounded-sm p-8 mt-4">
          <div className="flex items-start gap-3 border-b border-ink-line pb-5 mb-6">
            <AlertCircle className="w-6 h-6 text-warn shrink-0 mt-1" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Checkout incomplete</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                Your payment was not completed.
              </h1>
              <p className="text-sm text-ink-muted mt-2 leading-relaxed">
                No charge was made. The TRCA Assessment is gated by a one-time payment of
                <strong className="text-ink font-num"> ₹14,000</strong> per attempt. You can retry below.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { k: "6", l: "Capability sections" },
              { k: "<10m", l: "To complete" },
              { k: "1", l: "Branded result page" },
            ].map((x) => (
              <div key={x.l} className="border border-ink-line rounded-sm p-4">
                <div className="font-num font-bold text-2xl text-brand">{x.k}</div>
                <div className="stat-label mt-1">{x.l}</div>
              </div>
            ))}
          </div>

          <div className="space-y-3 mb-8">
            <h2 className="font-display font-semibold tracking-tight">What you get for ₹14,000</h2>
            <ul className="text-sm space-y-2">
              {[
                "Governance, capacity, compliance, pricing, delivery and risk audit",
                "Section-level scores + overall readiness band",
                "Tailored recommendations based on your current win rate",
                "Branded result page you can share with your leadership team",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-good mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-secondary border border-ink-line p-4 rounded-sm text-[12px] text-ink-muted leading-relaxed mb-6">
            <strong className="text-ink">Refund policy:</strong> No refund once the assessment is started.
            Secure payments via Stripe — UPI, RuPay, card and netbanking supported.
          </div>

          {error && (
            <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm flex items-start gap-2 mb-4" data-testid="qualifier-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <Link to="/dashboard" className="text-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline" data-testid="qualifier-back-link">
              Back to dashboard
            </Link>
            <button
              onClick={retry}
              disabled={loading}
              data-testid="qualifier-retry-btn"
              className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-2 transition-colors"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Retry payment <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
